import { Action } from "redux";

import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Filter } from "@/mk/bazaar/product/filter/filterUtils";

export interface ViewsCounts {
    [productId: number]: number;
}

export interface MyBazaarLoadApiAction extends Action {
    readonly username: string;
    readonly filter: Filter;
}

export const MY_BAZAAR_LOAD_API_REQUEST = "MY_BAZAAR_LOAD_API_REQUEST";

export interface MyBazaarLoadApiRequestAction extends MyBazaarLoadApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_LOAD_API_REQUEST;
}

export interface MyBazaarLoadNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
        productsAdding: number[];
        lastCreatedProduct: number;
        lastCreatedProductStroller: number;
        recentlyCreatedProduct: number;
        recentlyCreatedProductStroller: number;
    };
}

export const MY_BAZAAR_LOAD_API_SUCCESS = "MY_BAZAAR_LOAD_API_SUCCESS";

export interface MyBazaarLoadApiSuccessAction
    extends MyBazaarLoadApiAction, APISuccessAction<MyBazaarLoadNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_LOAD_API_SUCCESS;
    readonly facets: FacetsData;
    readonly hits: number;
    readonly viewsCounts: ViewsCounts;
    readonly repostableCount: number;
}

export const MY_BAZAAR_LOAD_API_FAILURE = "MY_BAZAAR_LOAD_API_FAILURE";

export interface MyBazaarLoadApiFailureAction extends MyBazaarLoadApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_LOAD_API_FAILURE;
}

export const myBazaarLoadApi = {
    request: (username: string, filter: Filter): MyBazaarLoadApiRequestAction => ({
        type: MY_BAZAAR_LOAD_API_REQUEST,
        username,
        filter,
    }),
    success: (
        username: string,
        filter: Filter,
        facets: FacetsData,
        hits: number,
        viewsCounts: ViewsCounts,
        repostableCount: number,
        response: MyBazaarLoadNormalizedResponse,
    ): MyBazaarLoadApiSuccessAction => ({
        type: MY_BAZAAR_LOAD_API_SUCCESS,
        username,
        filter,
        facets,
        hits,
        viewsCounts,
        repostableCount,
        response,
    }),
    failure: (username: string, filter: Filter, error: string): MyBazaarLoadApiFailureAction => ({
        type: MY_BAZAAR_LOAD_API_FAILURE,
        username,
        filter,
        error,
    }),
};

export const MY_BAZAAR_LOAD = "MY_BAZAAR_LOAD";

export interface MyBazaarLoadAction extends MyBazaarLoadApiAction {
    readonly type: typeof MY_BAZAAR_LOAD;
    readonly location: HistoryLocationWithState;
}

export const myBazaarLoad = (
    username: string,
    filter: Filter,
    location: HistoryLocationWithState,
): MyBazaarLoadAction => ({
    type: MY_BAZAAR_LOAD,
    username,
    filter,
    location,
});

export const MY_BAZAAR_INVALIDATE = "MY_BAZAAR_INVALIDATE";

export interface MyBazaarInvalidateAction extends Action {
    readonly type: typeof MY_BAZAAR_INVALIDATE;
    readonly username: string;
}

export const myBazaarInvalidate = (username: string): MyBazaarInvalidateAction => ({
    type: MY_BAZAAR_INVALIDATE,
    username,
});

export interface MyBazaarDeleteProductsApiAction extends Action {
    readonly username: string;
    readonly selected: number[];
}

export const MY_BAZAAR_DELETE_PRODUCTS_API_REQUEST = "MY_BAZAAR_DELETE_PRODUCTS_API_REQUEST";

export interface MyBazaarDeleteProductsApiRequestAction
    extends MyBazaarDeleteProductsApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_DELETE_PRODUCTS_API_REQUEST;
}

export interface MyBazaarDeleteProductsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
    };
}

export const MY_BAZAAR_DELETE_PRODUCTS_API_SUCCESS = "MY_BAZAAR_DELETE_PRODUCTS_API_SUCCESS";

export interface MyBazaarDeleteProductsApiSuccessAction
    extends
        MyBazaarDeleteProductsApiAction,
        APISuccessAction<MyBazaarDeleteProductsApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_DELETE_PRODUCTS_API_SUCCESS;
}

export const MY_BAZAAR_DELETE_PRODUCTS_API_FAILURE = "MY_BAZAAR_DELETE_PRODUCTS_API_FAILURE";

export interface MyBazaarDeleteProductsApiFailureAction
    extends MyBazaarDeleteProductsApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_DELETE_PRODUCTS_API_FAILURE;
}

export const myBazaarDeleteProductsApi = {
    request: (username: string, selected: number[]): MyBazaarDeleteProductsApiRequestAction => ({
        type: MY_BAZAAR_DELETE_PRODUCTS_API_REQUEST,
        username,
        selected,
    }),
    success: (
        username: string,
        selected: number[],
        response: MyBazaarDeleteProductsApiNormalizedResponse,
    ): MyBazaarDeleteProductsApiSuccessAction => ({
        type: MY_BAZAAR_DELETE_PRODUCTS_API_SUCCESS,
        username,
        selected,
        response,
    }),
    failure: (
        username: string,
        selected: number[],
        error: string,
    ): MyBazaarDeleteProductsApiFailureAction => ({
        type: MY_BAZAAR_DELETE_PRODUCTS_API_FAILURE,
        username,
        selected,
        error,
    }),
};

export const MY_BAZAAR_DELETE_PRODUCTS = "MY_BAZAAR_DELETE_PRODUCTS";

export interface MyBazaarDeleteProductsAction extends MyBazaarDeleteProductsApiAction {
    readonly type: typeof MY_BAZAAR_DELETE_PRODUCTS;
}

export const myBazaarDeleteProducts = (
    username: string,
    selected: number[],
): MyBazaarDeleteProductsAction => ({
    type: MY_BAZAAR_DELETE_PRODUCTS,
    username,
    selected,
});

export interface MyBazaarActivateProductsApiAction extends Action {
    readonly username: string;
    readonly selected: number[];
}

export const MY_BAZAAR_ACTIVATE_PRODUCTS_API_REQUEST = "MY_BAZAAR_ACTIVATE_PRODUCTS_API_REQUEST";

export interface MyBazaarActivateProductsApiRequestAction
    extends MyBazaarActivateProductsApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_ACTIVATE_PRODUCTS_API_REQUEST;
}

export interface MyBazaarActivateProductsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
    };
}

export const MY_BAZAAR_ACTIVATE_PRODUCTS_API_SUCCESS = "MY_BAZAAR_ACTIVATE_PRODUCTS_API_SUCCESS";

export interface MyBazaarActivateProductsApiSuccessAction
    extends
        MyBazaarActivateProductsApiAction,
        APISuccessAction<MyBazaarActivateProductsApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_ACTIVATE_PRODUCTS_API_SUCCESS;
}

export const MY_BAZAAR_ACTIVATE_PRODUCTS_API_FAILURE = "MY_BAZAAR_ACTIVATE_PRODUCTS_API_FAILURE";

export interface MyBazaarActivateProductsApiFailureAction
    extends MyBazaarActivateProductsApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_ACTIVATE_PRODUCTS_API_FAILURE;
}

export const myBazaarActivateProductsApi = {
    request: (username: string, selected: number[]): MyBazaarActivateProductsApiRequestAction => ({
        type: MY_BAZAAR_ACTIVATE_PRODUCTS_API_REQUEST,
        username,
        selected,
    }),
    success: (
        username: string,
        selected: number[],
        response: MyBazaarActivateProductsApiNormalizedResponse,
    ): MyBazaarActivateProductsApiSuccessAction => ({
        type: MY_BAZAAR_ACTIVATE_PRODUCTS_API_SUCCESS,
        username,
        selected,
        response,
    }),
    failure: (
        username: string,
        selected: number[],
        error: string,
    ): MyBazaarActivateProductsApiFailureAction => ({
        type: MY_BAZAAR_ACTIVATE_PRODUCTS_API_FAILURE,
        username,
        selected,
        error,
    }),
};

export const MY_BAZAAR_ACTIVATE_PRODUCTS = "MY_BAZAAR_ACTIVATE_PRODUCTS";

export interface MyBazaarActivateProductsAction extends MyBazaarActivateProductsApiAction {
    readonly type: typeof MY_BAZAAR_ACTIVATE_PRODUCTS;
}

export const myBazaarActivateProducts = (
    username: string,
    selected: number[],
): MyBazaarActivateProductsAction => ({
    type: MY_BAZAAR_ACTIVATE_PRODUCTS,
    username,
    selected,
});

export interface MyBazaarDeactivateProductsApiAction extends Action {
    readonly username: string;
    readonly selected: number[];
}

export const MY_BAZAAR_DEACTIVATE_PRODUCTS_API_REQUEST =
    "MY_BAZAAR_DEACTIVATE_PRODUCTS_API_REQUEST";

export interface MyBazaarDeactivateProductsApiRequestAction
    extends MyBazaarDeactivateProductsApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_DEACTIVATE_PRODUCTS_API_REQUEST;
}

export interface MyBazaarDeactivateProductsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
    };
}

export const MY_BAZAAR_DEACTIVATE_PRODUCTS_API_SUCCESS =
    "MY_BAZAAR_DEACTIVATE_PRODUCTS_API_SUCCESS";

export interface MyBazaarDeactivateProductsApiSuccessAction
    extends
        MyBazaarDeactivateProductsApiAction,
        APISuccessAction<MyBazaarDeactivateProductsApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_DEACTIVATE_PRODUCTS_API_SUCCESS;
}

export const MY_BAZAAR_DEACTIVATE_PRODUCTS_API_FAILURE =
    "MY_BAZAAR_DEACTIVATE_PRODUCTS_API_FAILURE";

export interface MyBazaarDeactivateProductsApiFailureAction
    extends MyBazaarDeactivateProductsApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_DEACTIVATE_PRODUCTS_API_FAILURE;
}

export const myBazaarDeactivateProductsApi = {
    request: (
        username: string,
        selected: number[],
    ): MyBazaarDeactivateProductsApiRequestAction => ({
        type: MY_BAZAAR_DEACTIVATE_PRODUCTS_API_REQUEST,
        username,
        selected,
    }),
    success: (
        username: string,
        selected: number[],
        response: MyBazaarDeactivateProductsApiNormalizedResponse,
    ): MyBazaarDeactivateProductsApiSuccessAction => ({
        type: MY_BAZAAR_DEACTIVATE_PRODUCTS_API_SUCCESS,
        username,
        selected,
        response,
    }),
    failure: (
        username: string,
        selected: number[],
        error: string,
    ): MyBazaarDeactivateProductsApiFailureAction => ({
        type: MY_BAZAAR_DEACTIVATE_PRODUCTS_API_FAILURE,
        username,
        selected,
        error,
    }),
};

export const MY_BAZAAR_DEACTIVATE_PRODUCTS = "MY_BAZAAR_DEACTIVATE_PRODUCTS";

export interface MyBazaarDeactivateProductsAction extends MyBazaarDeactivateProductsApiAction {
    readonly type: typeof MY_BAZAAR_DEACTIVATE_PRODUCTS;
}

export const myBazaarDeactivateProducts = (
    username: string,
    selected: number[],
): MyBazaarDeactivateProductsAction => ({
    type: MY_BAZAAR_DEACTIVATE_PRODUCTS,
    username,
    selected,
});

export interface MyBazaarRepostProductsApiAction extends Action {
    readonly username: string;
    readonly selected: number[];
}

export const MY_BAZAAR_REPOST_PRODUCTS_API_REQUEST = "MY_BAZAAR_REPOST_PRODUCTS_API_REQUEST";

export interface MyBazaarRepostProductsApiRequestAction
    extends MyBazaarRepostProductsApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_REPOST_PRODUCTS_API_REQUEST;
}

export interface MyBazaarRepostProductsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
    };
}

export const MY_BAZAAR_REPOST_PRODUCTS_API_SUCCESS = "MY_BAZAAR_REPOST_PRODUCTS_API_SUCCESS";

export interface MyBazaarRepostProductsApiSuccessAction
    extends
        MyBazaarRepostProductsApiAction,
        APISuccessAction<MyBazaarRepostProductsApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_REPOST_PRODUCTS_API_SUCCESS;
    readonly count: number;
}

export const MY_BAZAAR_REPOST_PRODUCTS_API_FAILURE = "MY_BAZAAR_REPOST_PRODUCTS_API_FAILURE";

export interface MyBazaarRepostProductsApiFailureAction
    extends MyBazaarRepostProductsApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_REPOST_PRODUCTS_API_FAILURE;
}

export const myBazaarRepostProductsApi = {
    request: (username: string, selected: number[]): MyBazaarRepostProductsApiRequestAction => ({
        type: MY_BAZAAR_REPOST_PRODUCTS_API_REQUEST,
        username,
        selected,
    }),
    success: (
        username: string,
        selected: number[],
        count: number,
        response: MyBazaarRepostProductsApiNormalizedResponse,
    ): MyBazaarRepostProductsApiSuccessAction => ({
        type: MY_BAZAAR_REPOST_PRODUCTS_API_SUCCESS,
        username,
        selected,
        count,
        response,
    }),
    failure: (
        username: string,
        selected: number[],
        error: string,
    ): MyBazaarRepostProductsApiFailureAction => ({
        type: MY_BAZAAR_REPOST_PRODUCTS_API_FAILURE,
        username,
        selected,
        error,
    }),
};

export const MY_BAZAAR_REPOST_PRODUCTS = "MY_BAZAAR_REPOST_PRODUCTS";

export interface MyBazaarRepostProductsAction extends MyBazaarRepostProductsApiAction {
    readonly type: typeof MY_BAZAAR_REPOST_PRODUCTS;
}

export const myBazaarRepostProducts = (
    username: string,
    selected: number[],
): MyBazaarRepostProductsAction => ({
    type: MY_BAZAAR_REPOST_PRODUCTS,
    username,
    selected,
});

// Repost All

export interface MyBazaarRepostAllApiAction extends Action {
    readonly username: string;
}

export const MY_BAZAAR_REPOST_ALL_API_REQUEST = "MY_BAZAAR_REPOST_ALL_API_REQUEST";

export interface MyBazaarRepostAllApiRequestAction
    extends MyBazaarRepostAllApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_REPOST_ALL_API_REQUEST;
}

export interface MyBazaarRepostAllApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        products: number[];
    };
}

export const MY_BAZAAR_REPOST_ALL_API_SUCCESS = "MY_BAZAAR_REPOST_ALL_API_SUCCESS";

export interface MyBazaarRepostAllApiSuccessAction
    extends MyBazaarRepostAllApiAction, APISuccessAction<MyBazaarRepostAllApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_REPOST_ALL_API_SUCCESS;
}

export const MY_BAZAAR_REPOST_ALL_API_FAILURE = "MY_BAZAAR_REPOST_ALL_API_FAILURE";

export interface MyBazaarRepostAllApiFailureAction
    extends MyBazaarRepostAllApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_REPOST_ALL_API_FAILURE;
}

export const myBazaarRepostAllApi = {
    request: (username: string): MyBazaarRepostAllApiRequestAction => ({
        type: MY_BAZAAR_REPOST_ALL_API_REQUEST,
        username,
    }),
    success: (
        username: string,
        response: MyBazaarRepostAllApiNormalizedResponse,
    ): MyBazaarRepostAllApiSuccessAction => ({
        type: MY_BAZAAR_REPOST_ALL_API_SUCCESS,
        username,
        response,
    }),
    failure: (username: string, error: string): MyBazaarRepostAllApiFailureAction => ({
        type: MY_BAZAAR_REPOST_ALL_API_FAILURE,
        username,
        error,
    }),
};

export const MY_BAZAAR_REPOST_ALL = "MY_BAZAAR_REPOST_ALL";

export interface MyBazaarRepostAllAction extends MyBazaarRepostAllApiAction {
    readonly type: typeof MY_BAZAAR_REPOST_ALL;
}

export const myBazaarRepostAll = (username: string): MyBazaarRepostAllAction => ({
    type: MY_BAZAAR_REPOST_ALL,
    username,
});

// Clear last created product

export const MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT = "MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT";

export interface MyBazaarClearLastCreatedProductAction extends Action {
    readonly type: typeof MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT;
    readonly disable: boolean;
}

export const myBazaarClearLastCreatedProduct = (
    disable: boolean,
): MyBazaarClearLastCreatedProductAction => ({
    type: MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT,
    disable,
});

// Hide announcement of adding stroller review

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewApiAction extends Action {
    readonly username: string;
    readonly strollerId: number;
}

export const MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_REQUEST =
    "MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_REQUEST";

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewApiRequestAction
    extends MyBazaarHideAnnouncementOfAddingStrollerReviewApiAction, APIRequestAction {
    readonly type: typeof MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_REQUEST;
}

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        recentlyCreatedProduct: number;
        recentlyCreatedProductStroller: number;
    };
}

export const MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_SUCCESS =
    "MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_SUCCESS";

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewApiSuccessAction
    extends
        MyBazaarHideAnnouncementOfAddingStrollerReviewApiAction,
        APISuccessAction<MyBazaarHideAnnouncementOfAddingStrollerReviewApiNormalizedResponse> {
    readonly type: typeof MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_SUCCESS;
}

export const MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_FAILURE =
    "MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_FAILURE";

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewApiFailureAction
    extends MyBazaarHideAnnouncementOfAddingStrollerReviewApiAction, APIFailureAction {
    readonly type: typeof MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_FAILURE;
}

export const myBazaarHideAnnouncementOfAddingStrollerReviewApi = {
    request: (
        username: string,
        strollerId: number,
    ): MyBazaarHideAnnouncementOfAddingStrollerReviewApiRequestAction => ({
        type: MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_REQUEST,
        username,
        strollerId,
    }),
    success: (
        username: string,
        strollerId: number,
        response: MyBazaarHideAnnouncementOfAddingStrollerReviewApiNormalizedResponse,
    ): MyBazaarHideAnnouncementOfAddingStrollerReviewApiSuccessAction => ({
        type: MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_SUCCESS,
        username,
        strollerId,
        response,
    }),
    failure: (
        username: string,
        strollerId: number,
        error: string,
    ): MyBazaarHideAnnouncementOfAddingStrollerReviewApiFailureAction => ({
        type: MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW_API_FAILURE,
        username,
        strollerId,
        error,
    }),
};

export const MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW =
    "MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW";

export interface MyBazaarHideAnnouncementOfAddingStrollerReviewAction extends MyBazaarHideAnnouncementOfAddingStrollerReviewApiAction {
    readonly type: typeof MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW;
}

export const myBazaarHideAnnouncementOfAddingStrollerReview = (
    username: string,
    strollerId: number,
): MyBazaarHideAnnouncementOfAddingStrollerReviewAction => ({
    type: MY_BAZAAR_HIDE_ANNOUNCEMENT_OF_ADDING_STROLLER_REVIEW,
    username,
    strollerId,
});
